import { z } from 'zod'

/**
 * Error infrastructure — the one domain-agnostic piece of `@hostingportal/schemas/core`
 * that PLAN-00 seeds at bootstrap, because the frozen capability dispatcher
 * (`@hostingportal/capabilities/src/core`) imports `HostingPortalError` + `CapabilityError` to
 * compile. PLAN-01 owns the rest of `@hostingportal/schemas` and may extend this file;
 * it should not change the `HostingPortalError` / `CapabilityError` shape without a
 * coordinated PR, since the dispatcher depends on it.
 */

/** Field-scoped server validation error. */
export const FieldError = z.object({
  path: z.array(z.union([z.string(), z.number()])),
  message: z.string(),
  code: z.string().optional(),
})

export type FieldError = z.infer<typeof FieldError>

/** Wire shape returned by the capability dispatcher on failure. */
export const CapabilityError = z.object({
  code: z.string(),
  message: z.string(),
  correlationId: z.string(),
  fieldErrors: z.array(FieldError).optional(),
  retryable: z.boolean().default(false),
})

export type CapabilityError = z.infer<typeof CapabilityError>

/**
 * Thrown by the SDK / capability layer; caught at the mutation site and surfaced
 * to the user. Throw `HostingPortalError` from SDK and capability layers.
 */
export class HostingPortalError extends Error {
  readonly code: string
  readonly correlationId: string
  readonly fieldErrors?: FieldError[]
  readonly retryable: boolean

  constructor(input: CapabilityError) {
    super(input.message)
    this.name = 'HostingPortalError'
    this.code = input.code
    this.correlationId = input.correlationId
    this.fieldErrors = input.fieldErrors
    this.retryable = input.retryable
  }
}
